// Tracking de la profondeur de scroll : envoie un event "Scroll - 25%/50%/75%/100%"
// la premiere fois que l'utilisateur depasse chaque seuil. Reset a chaque nouvelle
// pageview (via resetScrollForPage). Skip si la page tient dans le viewport
// (aucun scroll possible) ou si scrollTop=0 (mesure passive ignoree).

import { trackEvent } from './event';

const SCROLL_THRESHOLDS = [25, 50, 75, 100];
const THROTTLE_MS = 250;
const NO_SCROLL_MARGIN_PX = 50;

let scrollTriggered = new Set<number>();
let lastTickAt = 0;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let scrollListenerAttached = false;
let currentPath = '';

function trackingDisabled(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie.includes('matomo_ignore=1');
}

function currentScrollTop(): number {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  const doc = document.documentElement;
  const body = document.body;
  return window.scrollY || doc.scrollTop || (body && body.scrollTop) || 0;
}

function computeScrollPercent(): number {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  const doc = document.documentElement;
  const body = document.body;
  const scrollTop = currentScrollTop();
  const viewportH = window.innerHeight || doc.clientHeight || 0;
  const totalH = Math.max(
    doc.scrollHeight, (body && body.scrollHeight) || 0,
    doc.offsetHeight, (body && body.offsetHeight) || 0,
    doc.clientHeight,
  );
  if (totalH <= 0) return 0;
  return Math.min(100, Math.round(((scrollTop + viewportH) / totalH) * 100));
}

function canScroll(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  const doc = document.documentElement;
  const body = document.body;
  const viewportH = window.innerHeight || doc.clientHeight || 0;
  const totalH = Math.max(
    doc.scrollHeight, (body && body.scrollHeight) || 0,
    doc.offsetHeight, (body && body.offsetHeight) || 0,
  );
  return totalH > viewportH + NO_SCROLL_MARGIN_PX;
}

function reportThresholds(percent: number): void {
  for (const t of SCROLL_THRESHOLDS) {
    if (percent >= t && !scrollTriggered.has(t)) {
      scrollTriggered.add(t);
      trackEvent('Scroll', `${t}%`, { name: currentPath });
    }
  }
}

function onScroll(): void {
  if (trackingDisabled()) return;
  if (!canScroll()) return;
  if (currentScrollTop() <= 0) return;

  const now = Date.now();
  const elapsed = now - lastTickAt;
  if (elapsed >= THROTTLE_MS) {
    lastTickAt = now;
    reportThresholds(computeScrollPercent());
  } else if (!pendingTimer) {
    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      lastTickAt = Date.now();
      reportThresholds(computeScrollPercent());
    }, THROTTLE_MS - elapsed);
  }
}

export function resetScrollForPage(virtualPath: string): void {
  scrollTriggered.clear();
  lastTickAt = 0;
  if (pendingTimer) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  currentPath = virtualPath || (typeof window !== 'undefined' ? window.location.pathname : '/');
}

export function initScrollTracker(): void {
  if (typeof window === 'undefined' || scrollListenerAttached) return;
  window.addEventListener('scroll', onScroll, { passive: true });
  scrollListenerAttached = true;
}
